import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from 'reactstrap'

import Header from '../../../components/Headers/Header'
import { notify } from '../../../utils/index_tagging_tool'
import {
  downloadAiDimensionTagsFile,
  estimateAiDimensionCost,
  getAiDimension,
  getAiDimensionTags,
  getSavedDimensionLinkOptions,
  importAiDimensionTagsFile,
  replaceAiDimensionTags,
  updateAiDimension,
} from '../../api_methods'
import Loading from '../../components/Loading'
import PageTitle from '../../components/PageTitle'
import './AiDimensions.scss'

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'Non stimato'
  }
  return `$${Number(value).toFixed(4)}`
}

const getModeLabel = (mode) => (mode === 'repeated' ? 'Lista' : 'Normale')

const buildTagRow = () => ({
  tag_it: '',
  tag_en: '',
})

const EMPTY_TAGS_PAYLOAD = {
  read_only_tags: false,
  local_tags: [],
  effective_tags: [],
}

const buildConfigState = (dimension) => ({
  name: dimension?.name || '',
  active: Boolean(dimension?.active),
  prompt: dimension?.prompt || '',
  bq_mode: dimension?.bq_mode || 'nullable',
  linked_saved_dimension_id: dimension?.linked_saved_dimension?.id
    ? String(dimension.linked_saved_dimension.id)
    : '',
})

const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.URL.revokeObjectURL(url)
}

export default function AiDimensionsPage(props) {
  const { reportId, aiDimensionId } = props.match.params
  const [dimension, setDimension] = useState(null)
  const [configForm, setConfigForm] = useState(buildConfigState(null))
  const [savedDimensions, setSavedDimensions] = useState([])
  const [tagsPayload, setTagsPayload] = useState(EMPTY_TAGS_PAYLOAD)
  const [editableTags, setEditableTags] = useState([])
  const [estimate, setEstimate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingError, setLoadingError] = useState('')
  const [savingConfig, setSavingConfig] = useState(false)
  const [savingTags, setSavingTags] = useState(false)
  const [estimating, setEstimating] = useState(false)
  const [importFile, setImportFile] = useState(null)
  const [importingFile, setImportingFile] = useState(false)

  const selectedSavedDimension = useMemo(() => {
    return savedDimensions.find(
      (savedDimension) =>
        String(savedDimension.id) === String(configForm.linked_saved_dimension_id || '')
    )
  }, [configForm.linked_saved_dimension_id, savedDimensions])

  const effectiveTags = tagsPayload.effective_tags || []
  const readOnlyTags = Boolean(tagsPayload.read_only_tags)
  const latestEstimatedCost = estimate?.estimated_cost ?? dimension?.last_estimated_cost

  const applyTagsPayload = (payload) => {
    const nextPayload = payload || EMPTY_TAGS_PAYLOAD
    setTagsPayload(nextPayload)
    setEditableTags((nextPayload.local_tags || []).map((tag) => ({ ...tag })))
  }

  const loadPage = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true)
    }
    setLoadingError('')

    try {
      const [dimensionData, tagsData] = await Promise.all([
        getAiDimension(aiDimensionId),
        getAiDimensionTags(aiDimensionId),
      ])

      setDimension(dimensionData)
      setConfigForm(buildConfigState(dimensionData))
      applyTagsPayload(tagsData)
    } catch (error) {
      setLoadingError('Impossibile caricare il dettaglio della dimensione AI.')
    } finally {
      setLoading(false)
    }
  }

  const loadSavedDimensions = async () => {
    try {
      const savedDimensionsData = await getSavedDimensionLinkOptions()
      setSavedDimensions(savedDimensionsData || [])
    } catch (error) {
      notify('Impossibile caricare l elenco delle dimensioni salvate disponibili.', 'warning')
    }
  }

  useEffect(() => {
    loadPage()
    loadSavedDimensions()
  }, [reportId, aiDimensionId])

  const updateConfigField = (field, value) => {
    setConfigForm((current) => ({
      ...current,
      [field]: value,
    }))
  }

  const handleConfigSave = async (event) => {
    event.preventDefault()

    if (!configForm.name.trim()) {
      notify('Il nome e obbligatorio.', 'warning')
      return
    }

    if (!configForm.prompt.trim()) {
      notify('Il prompt e obbligatorio.', 'warning')
      return
    }

    setSavingConfig(true)
    try {
      const payload = {
        name: configForm.name.trim(),
        display_name: configForm.name.trim(),
        active: configForm.active,
        prompt: configForm.prompt.trim(),
        bq_mode: configForm.bq_mode,
        linked_saved_dimension_id: configForm.linked_saved_dimension_id || '',
      }
      const updated = await updateAiDimension(aiDimensionId, payload)
      const refreshedTags = await getAiDimensionTags(aiDimensionId)
      setDimension(updated)
      setConfigForm(buildConfigState(updated))
      applyTagsPayload(refreshedTags)
      notify('Configurazione dimensione AI salvata.', 'success')
    } catch (error) {
      notify(error?.response?.data?.error || 'Errore durante il salvataggio della configurazione.', 'error')
    } finally {
      setSavingConfig(false)
    }
  }

  const addTagRow = () => {
    setEditableTags((current) => [...current, buildTagRow()])
  }

  const removeTagRow = (index) => {
    setEditableTags((current) => current.filter((_tag, currentIndex) => currentIndex !== index))
  }

  const updateTagField = (index, field, value) => {
    setEditableTags((current) =>
      current.map((tag, currentIndex) =>
        currentIndex === index
          ? {
              ...tag,
              [field]: value,
            }
          : tag
      )
    )
  }

  const handleTagsSave = async () => {
    setSavingTags(true)
    try {
      const cleanTags = editableTags.map((tag, index) => ({
        priority: index,
        tag_it: (tag.tag_it || '').trim(),
        tag_en: (tag.tag_en || '').trim(),
      }))
      const response = await replaceAiDimensionTags(aiDimensionId, cleanTags)
      applyTagsPayload(response)
      notify('Tag locali salvati.', 'success')
    } catch (error) {
      notify(error?.response?.data?.error || 'Errore durante il salvataggio dei tag.', 'error')
    } finally {
      setSavingTags(false)
    }
  }

  const handleImport = async () => {
    if (!importFile) {
      notify('Seleziona prima un file CSV o XLSX.', 'warning')
      return
    }

    const formData = new FormData()
    formData.append('file', importFile)

    setImportingFile(true)
    try {
      const response = await importAiDimensionTagsFile(aiDimensionId, formData)
      applyTagsPayload(response)
      setImportFile(null)
      notify('Tag importati correttamente.', 'success')
    } catch (error) {
      notify(error?.response?.data?.error || 'Errore durante l importazione del file.', 'error')
    } finally {
      setImportingFile(false)
    }
  }

  const handleDownload = async () => {
    try {
      const response = await downloadAiDimensionTagsFile(aiDimensionId)
      downloadBlob(response.data, `${dimension.name || 'ai-dimension'}_tags.xlsx`)
    } catch (error) {
      notify('Impossibile scaricare il file dei tag.', 'error')
    }
  }

  const refreshEstimate = async () => {
    setEstimating(true)
    try {
      const response = await estimateAiDimensionCost(aiDimensionId)
      setEstimate(response?.estimate || null)
      notify('Stima aggiornata.', 'success')
    } catch (error) {
      notify(error?.response?.data?.error || 'Impossibile aggiornare la stima.', 'error')
    } finally {
      setEstimating(false)
    }
  }

  if (loading) {
    return (
      <>
        <Header iconClass="fa-solid fa-tags fa-xl" title="Tagging Tool" />
        <Row className="justify-content-center">
          <Col md={10}>
            <Card className="shadow">
              <CardBody>
                <p className="mb-3">Caricamento dettaglio dimensione AI in corso...</p>
                <Loading />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </>
    )
  }

  if (loadingError || !dimension) {
    return (
      <>
        <Header iconClass="fa-solid fa-tags fa-xl" title="Tagging Tool" />
        <Row className="justify-content-center" style={{ marginBottom: '100px' }}>
          <Col md={10}>
            <Card className="bg-secondary shadow flex">
              <CardHeader>
                <PageTitle
                  title="Dettaglio dimensione AI"
                  link={`/reporting/reports/seo/${reportId}`}
                  linkText="Torna al report SEO"
                />
              </CardHeader>
              <CardBody>
                <Alert color="danger" className="mb-3">
                  {loadingError || 'Errore durante il caricamento della pagina.'}
                </Alert>
                <Button color="primary" onClick={() => loadPage()}>
                  Riprova
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </>
    )
  }

  return (
    <>
      <Header iconClass="fa-solid fa-tags fa-xl" title="Tagging Tool" />
      <Row style={{ marginBottom: '100px' }}>
        <Col md={10}>
          <Card className="bg-secondary shadow flex">
            <CardHeader>
              <PageTitle
                title={`${dimension.display_name} - Dimensione AI`}
                subtitle={dimension.report_name}
                link={`/reporting/reports/seo/${reportId}`}
                linkText="Torna al report SEO"
              />
            </CardHeader>
            <CardBody className="pt-0 pt-md-4">
              <div className="ai-dimensions-summary mb-4">
                <Card className="shadow-sm">
                  <CardBody>
                    <div className="ai-muted-label">Modello</div>
                    <div className="ai-summary-copy">
                      <Badge color="primary" pill>
                        {dimension.model_name}
                      </Badge>
                    </div>
                  </CardBody>
                </Card>
                <Card className="shadow-sm">
                  <CardBody>
                    <div className="ai-muted-label">Output</div>
                    <div className="ai-summary-copy">{getModeLabel(dimension.bq_mode)}</div>
                  </CardBody>
                </Card>
                <Card className="shadow-sm">
                  <CardBody>
                    <div className="ai-muted-label">Tag effettivi</div>
                    <div className="ai-summary-value">{effectiveTags.length}</div>
                  </CardBody>
                </Card>
                <Card className="shadow-sm">
                  <CardBody>
                    <div className="ai-muted-label">Ultima stima</div>
                          <div className="ai-summary-copy">{formatCurrency(latestEstimatedCost)}</div>
                  </CardBody>
                </Card>
              </div>

              <Row>
                <Col xl={7}>
                  <Card className="shadow-sm mb-4">
                    <CardHeader className="border-0">
                      <h3 className="mb-1">Configurazione</h3>
                      <div className="text-muted small">
                        Prompt, nome, formato di output e collegamento opzionale a una dimensione salvata.
                      </div>
                    </CardHeader>
                    <CardBody>
                      <Form onSubmit={handleConfigSave}>
                        <Row>
                          <Col md={12}>
                            <FormGroup>
                              <Label for="ai-dimension-config-name">Nome</Label>
                              <Input
                                id="ai-dimension-config-name"
                                onChange={(event) => updateConfigField('name', event.target.value)}
                                value={configForm.name}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="ai-dimension-config-mode">Modalita output</Label>
                              <Input
                                id="ai-dimension-config-mode"
                                onChange={(event) => updateConfigField('bq_mode', event.target.value)}
                                type="select"
                                value={configForm.bq_mode}
                              >
                                <option value="nullable">Normale</option>
                                <option value="repeated">Lista</option>
                              </Input>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="ai-dimension-config-link">Dimensione salvata collegata</Label>
                              <Input
                                id="ai-dimension-config-link"
                                onChange={(event) =>
                                  updateConfigField('linked_saved_dimension_id', event.target.value)
                                }
                                type="select"
                                value={configForm.linked_saved_dimension_id}
                              >
                                <option value="">Nessun collegamento</option>
                                {(savedDimensions || []).map((savedDimension) => (
                                  <option key={savedDimension.id} value={savedDimension.id}>
                                    {savedDimension.display_name} (
                                    {savedDimension.group?.name || savedDimension.group})
                                  </option>
                                ))}
                              </Input>
                            </FormGroup>
                          </Col>
                        </Row>

                        <FormGroup>
                          <Label for="ai-dimension-config-prompt">Prompt</Label>
                          <Input
                            id="ai-dimension-config-prompt"
                            onChange={(event) => updateConfigField('prompt', event.target.value)}
                            rows="8"
                            type="textarea"
                            value={configForm.prompt}
                          />
                        </FormGroup>

                        <Button color="primary" disabled={savingConfig} type="submit">
                          {savingConfig ? 'Salvataggio...' : 'Salva configurazione'}
                        </Button>
                      </Form>
                    </CardBody>
                  </Card>

                  <Card className="shadow-sm">
                    <CardHeader className="border-0">
                      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center">
                        <div>
                          <h3 className="mb-1">Tassonomia</h3>
                          <div className="text-muted small">
                            Tag locali modificabili oppure tassonomia live letta da una dimensione salvata.
                          </div>
                        </div>
                        <div className="d-flex flex-wrap mt-3 mt-lg-0">
                          <Button
                            className="mr-2 mb-2"
                            color="primary"
                            outline
                            onClick={handleDownload}
                          >
                            Esporta tag
                          </Button>
                          {!readOnlyTags && (
                            <Button className="mb-2" color="primary" outline onClick={addTagRow}>
                              Aggiungi tag
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardBody>
                      {readOnlyTags && (
                        <Alert color="info">
                          Questa dimensione AI e collegata a{' '}
                          <strong>{selectedSavedDimension?.display_name || dimension.linked_saved_dimension?.display_name}</strong>.
                          I tag locali sono bloccati. Se scolleghi la dimensione salvata, il sistema conservera
                          l ultimo snapshot locale per ripartire da quello.
                        </Alert>
                      )}

                      {!readOnlyTags && (
                        <>
                          <Alert color="light">
                            Import accettato: file CSV o XLSX con colonne <code>tag_it</code> e{' '}
                            <code>tag_en</code>. <code>tag_it</code> e obbligatoria.
                          </Alert>
                          <div className="d-flex flex-column flex-lg-row align-items-lg-center mb-3">
                            <Input
                              className="mr-lg-3 mb-2 mb-lg-0"
                              onChange={(event) => setImportFile(event.target.files?.[0] || null)}
                              type="file"
                              accept=".csv,.xlsx,.xls"
                            />
                            <Button color="primary" disabled={importingFile} onClick={handleImport}>
                              {importingFile ? 'Import in corso...' : 'Importa file'}
                            </Button>
                          </div>
                        </>
                      )}

                      {!readOnlyTags && (
                        <div className="table-responsive">
                          <Table className="ai-tag-table" responsive>
                            <thead>
                              <tr>
                                <th className="w-25">Tag IT</th>
                                <th className="w-25">Tag EN</th>
                                <th className="w-25">Anteprima</th>
                                <th className="text-right">Azioni</th>
                              </tr>
                            </thead>
                            <tbody>
                              {editableTags.length === 0 && (
                                <tr>
                                  <td colSpan="4" className="text-muted">
                                    Nessun tag locale configurato.
                                  </td>
                                </tr>
                              )}
                              {editableTags.map((tag, index) => (
                                <tr key={`editable-tag-${index}`}>
                                  <td>
                                    <Input
                                      onChange={(event) =>
                                        updateTagField(index, 'tag_it', event.target.value)
                                      }
                                      value={tag.tag_it || ''}
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      onChange={(event) =>
                                        updateTagField(index, 'tag_en', event.target.value)
                                      }
                                      value={tag.tag_en || ''}
                                    />
                                  </td>
                                  <td className="align-middle">
                                    <span className="ai-tag-pill">{tag.tag_it || 'Tag vuoto'}</span>
                                  </td>
                                  <td className="text-right">
                                    <Button
                                      color="danger"
                                      outline
                                      size="sm"
                                      onClick={() => removeTagRow(index)}
                                    >
                                      Rimuovi
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}

                      <div className="mt-4">
                        <h4 className="mb-3">Tag effettivi</h4>
                        {effectiveTags.length === 0 ? (
                          <Alert color="light" className="mb-0">
                            Nessun tag disponibile per questa dimensione AI.
                          </Alert>
                        ) : (
                          <div className="ai-tags-list">
                            {effectiveTags.map((tag, index) => (
                              <span className="ai-tag-pill" key={`${tag.tag_it}-${index}`}>
                                {tag.tag_it}
                                {tag.tag_en ? ` / ${tag.tag_en}` : ''}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {!readOnlyTags && (
                        <Button
                          className="mt-4"
                          color="primary"
                          disabled={savingTags}
                          onClick={handleTagsSave}
                        >
                          {savingTags ? 'Salvataggio...' : 'Salva tag locali'}
                        </Button>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={5} className="mt-4 mt-xl-0">
                  <Card className="shadow-sm mb-4">
                    <CardHeader className="border-0">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h3 className="mb-1">Costo stimato</h3>
                          <div className="text-muted small">
                            Stima singola dimensione aggiornata sul file keyword del report.
                          </div>
                        </div>
                        <Button color="primary" outline disabled={estimating} onClick={refreshEstimate}>
                          {estimating ? 'Aggiornamento...' : 'Aggiorna stima'}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardBody>
                      <div className="ai-tags-grid">
                        <div className="ai-stat-card">
                          <div className="ai-muted-label">Costo stimato</div>
                          <div>{formatCurrency(estimate?.estimated_cost)}</div>
                        </div>
                        <div className="ai-stat-card">
                          <div className="ai-muted-label">Keyword</div>
                          <div>{estimate?.keywords || 0}</div>
                        </div>
                        <div className="ai-stat-card">
                          <div className="ai-muted-label">Chiamate</div>
                          <div>{estimate?.estimated_calls || 0}</div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Card className="shadow-sm">
                    <CardHeader className="border-0">
                      <h3 className="mb-1">Note operative</h3>
                    </CardHeader>
                    <CardBody>
                      <Alert color="light">
                        Il modello e fisso su <strong>{dimension.model_name}</strong> in questa prima
                        versione. La modifica avviene solo lato backend.
                      </Alert>
                      <Alert color="light">
                        Modalita <strong>{getModeLabel(dimension.bq_mode)}</strong>: in BigQuery la
                        colonna verra esportata come {dimension.bq_mode === 'repeated' ? 'lista' : 'valore singolo'}.
                      </Alert>
                      <Alert color="light" className="mb-0">
                        Nella run standard del report, le colonne AI gia valorizzate vengono preservate.
                        L esecuzione solo AI aggiorna solo la parte AI.
                      </Alert>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}
